<template>
  <Teleport to="body">
    <Modal v-model="show">
      <ModalContent size="large">
        <template #header>
          <Heading level="h2">{{ t('settings_modal.title') }}</Heading>
        </template>

        <div class="tw-space-y-5">
          <section>
            <Heading level="h4">{{ t('settings_modal.preview.title') }}</Heading>
            <div class="tw-mt-3 tw-grid tw-gap-5 md:tw-grid-cols-2">
              <FormField
                v-model="previewQualityLocal"
                type="select"
                :label="t('settings_modal.preview.preview_quality.label')"
              >
                <option value="low">
                  {{ t('settings_modal.preview.preview_quality.options.low') }}
                </option>
                <option value="medium">
                  {{ t('settings_modal.preview.preview_quality.options.medium') }}
                </option>
                <option value="high">
                  {{ t('settings_modal.preview.preview_quality.options.high') }}
                </option>
              </FormField>

              <FormField
                v-model.number="previewFpsLocal"
                type="number"
                :label="t('settings_modal.preview.fps.label')"
                :min="1"
              />

              <FormField
                v-model.number="previewEncoderQualityLocal"
                type="number"
                :label="t('settings_modal.preview.encoder_quality.label')"
                :min="1"
              />

              <FormField
                v-model="previewEncoderTypeLocal"
                type="select"
                :label="t('settings_modal.preview.encoder_type.label')"
              >
                <option value="webp">webp</option>
                <option value="jpg">jpg</option>
              </FormField>

              <FormField
                :value="previewBitrateLocal"
                readonly
                :label="t('settings_modal.preview.approximate_bitrate.label')"
              />
            </div>
          </section>

          <section>
            <Heading level="h4">{{ t('settings_modal.monitor.title') }}</Heading>

            <div class="tw-mt-3 tw-grid tw-gap-5 md:tw-grid-cols-2">
              <FormField
                v-model="monitorQualityLocal"
                type="select"
                :label="t('settings_modal.monitor.preview_quality.label')"
              >
                <option value="low">
                  {{ t('settings_modal.monitor.preview_quality.options.low') }}
                </option>
                <option value="medium">
                  {{ t('settings_modal.monitor.preview_quality.options.medium') }}
                </option>
                <option value="high">
                  {{ t('settings_modal.monitor.preview_quality.options.high') }}
                </option>
              </FormField>

              <FormField
                v-model.number="monitorFpsLocal"
                type="number"
                :label="t('settings_modal.monitor.fps.label')"
                :min="1"
              />

              <FormField
                v-model.number="monitorEncoderQualityLocal"
                type="number"
                :label="t('settings_modal.monitor.encoder_quality.label')"
                :min="1"
              />

              <FormField
                v-model="monitorEncoderTypeLocal"
                type="select"
                :label="t('settings_modal.monitor.encoder_type.label')"
              >
                <option value="webp">webp</option>
                <option value="jpg">jpg</option>
              </FormField>

              <FormField
                :value="monitorBitrateLocal"
                readonly
                :label="t('settings_modal.monitor.approximate_bitrate.label')"
              />
            </div>
          </section>

          <section>
            <Heading level="h4">{{ t('settings_modal.audio.title') }}</Heading>

            <div class="tw-mt-3 tw-grid tw-gap-5 md:tw-grid-cols-2">
              <FormField
                v-model="audioMonitorMixLocal"
                type="select"
                :label="t('settings_modal.audio.monitor_mix.label')"
              >
                <option v-for="{ value, label } in audioMonitorMixOptions" :key="value" :value="value">
                  {{ label }}
                </option>
              </FormField>
            </div>
          </section>
        </div>

        <template #footer>
          <div class="tw-flex tw-w-full tw-justify-end tw-gap-2">
            <Button color="transparent" outlined @click="cancel">
              {{ t('common.cancel') }}
            </Button>
            <Button color="primary" @click="save">
              {{ t('common.save') }}
            </Button>
          </div>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button, FormField, Heading, Modal, ModalContent } from '@slideslive/fuse-kit/vue';

import estimatePreviewBitrate from '@/helpers/estimate_preview_bitrate.js';
import useSettingsStore from '@/stores/settings';

const show = defineModel();
const { t } = useI18n();

const settingsStore = useSettingsStore();
const {
  previewQuality,
  previewFps,
  previewEncoderQuality,
  previewEncoderType,
  monitorQuality,
  monitorFps,
  monitorEncoderQuality,
  monitorEncoderType,
  audioMonitorMix,
} = storeToRefs(settingsStore);

const previewQualityLocal = ref('low');
const previewFpsLocal = ref(2);
const previewEncoderQualityLocal = ref(5);
const previewEncoderTypeLocal = ref('webp');

const monitorQualityLocal = ref('medium');
const monitorFpsLocal = ref(12);
const monitorEncoderQualityLocal = ref(30);
const monitorEncoderTypeLocal = ref('webp');

const audioMonitorMixOptions = [
  { value: 'LR', label: 'L+R' },
  { value: 'LL', label: 'L+L' },
  { value: 'RR', label: 'R+R' },
  { value: 'mono', label: 'mono' },
];

const audioMonitorMixLocal = ref('LR');

const PREVIEW_PRESETS = {
  low: { fps: 2, encoderQuality: 5, encoderType: 'webp' },
  medium: { fps: 2, encoderQuality: 10, encoderType: 'webp' },
  high: { fps: 12, encoderQuality: 20, encoderType: 'webp' },
};

const MONITOR_PRESETS = {
  low: { fps: 5, encoderQuality: 10, encoderType: 'webp' },
  medium: { fps: 12, encoderQuality: 30, encoderType: 'webp' },
  high: { fps: 12, encoderQuality: 60, encoderType: 'webp' },
};

watch(
  () => show.value,
  (visible) => {
    if (!visible) return;

    previewQualityLocal.value = previewQuality.value;
    previewFpsLocal.value = previewFps.value;
    previewEncoderQualityLocal.value = previewEncoderQuality.value;
    previewEncoderTypeLocal.value = previewEncoderType.value;

    monitorQualityLocal.value = monitorQuality.value;
    monitorFpsLocal.value = monitorFps.value;
    monitorEncoderQualityLocal.value = monitorEncoderQuality.value;
    monitorEncoderTypeLocal.value = monitorEncoderType.value;

    audioMonitorMixLocal.value = audioMonitorMix.value;
  },
);

watch(
  () => previewQualityLocal.value,
  (quality) => {
    const preset = PREVIEW_PRESETS[quality];
    if (!preset) return;

    previewFpsLocal.value = preset.fps;
    previewEncoderQualityLocal.value = preset.encoderQuality;
    previewEncoderTypeLocal.value = preset.encoderType;
  },
);

watch(
  () => monitorQualityLocal.value,
  (quality) => {
    const preset = MONITOR_PRESETS[quality];
    if (!preset) return;

    monitorFpsLocal.value = preset.fps;
    monitorEncoderQualityLocal.value = preset.encoderQuality;
    monitorEncoderTypeLocal.value = preset.encoderType;
  },
);

const previewBitrateLocal = computed(() =>
  estimatePreviewBitrate({
    encoderType: previewEncoderTypeLocal,
    encoderQuality: previewEncoderQualityLocal,
    fps: previewFpsLocal,
  }),
);

const monitorBitrateLocal = computed(() =>
  estimatePreviewBitrate({
    encoderType: monitorEncoderTypeLocal,
    encoderQuality: monitorEncoderQualityLocal,
    fps: monitorFpsLocal,
  }),
);

function cancel() {
  show.value = false;
}

function save() {
  previewQuality.value = previewQualityLocal.value;
  previewFps.value = previewFpsLocal.value;
  previewEncoderQuality.value = previewEncoderQualityLocal.value;
  previewEncoderType.value = previewEncoderTypeLocal.value;
  monitorQuality.value = monitorQualityLocal.value;
  monitorFps.value = monitorFpsLocal.value;
  monitorEncoderQuality.value = monitorEncoderQualityLocal.value;
  monitorEncoderType.value = monitorEncoderTypeLocal.value;
  audioMonitorMix.value = audioMonitorMixLocal.value;
  show.value = false;
}
</script>
